import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "classes", "overrides", "className", "onClick", "size", "type", "isDisabled", "isFullWidth", "isLoading", "href", "target", "icon", "iconPosition"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Loader from '../../general/Loader';
import Icon from '../../general/Icon';
import Text from '../../typography/Text';
import { createUseStyles, useTheme } from '../../../utils/styles';
import styles from './styles';
var useStyles = createUseStyles(styles, 'Button');
var LOADER_SIZES = {
  big: 'tiny',
  medium: 'tiny',
  small: 'mini'
};
var ALLOWED_LOADING_TYPES = ['primary', 'primary-error'];
var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _classnames;
  var children = _ref.children,
    classesProp = _ref.classes,
    overridesProp = _ref.overrides,
    classNameProp = _ref.className,
    onClick = _ref.onClick,
    size = _ref.size,
    type = _ref.type,
    isDisabled = _ref.isDisabled,
    isFullWidth = _ref.isFullWidth,
    isLoading = _ref.isLoading,
    href = _ref.href,
    target = _ref.target,
    icon = _ref.icon,
    iconPosition = _ref.iconPosition,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var classes = useClasses(useStyles, classesProp);
  // Overrides
  var override = getOverrides(overridesProp, Button.overrides);

  // Classes
  var rootClassName = classnames(classes.root, (_classnames = {}, _defineProperty(_classnames, classes.primary, type === 'primary'), _defineProperty(_classnames, classes.primaryError, type === 'primary-error'), _defineProperty(_classnames, classes.secondary, type === 'secondary'), _defineProperty(_classnames, classes.secondaryError, type === 'secondary-error'), _defineProperty(_classnames, classes.terciary, type === 'terciary'), _defineProperty(_classnames, classes.dashed, type === 'dashed'), _defineProperty(_classnames, classes.promotion, type === 'promotion'), _defineProperty(_classnames, classes.primarySoft, type === 'primary-soft'), _defineProperty(_classnames, classes[size], size), _defineProperty(_classnames, classes.disabled, isDisabled), _defineProperty(_classnames, classes.loading, isLoading && ALLOWED_LOADING_TYPES.includes(type)), _defineProperty(_classnames, classes.fullWidth, isFullWidth), _defineProperty(_classnames, classes.iconLeft, icon && iconPosition === 'left'), _defineProperty(_classnames, classes.iconRight, icon && iconPosition === 'right'), _defineProperty(_classnames, classes.iconOnly, icon && !children), _classnames), classNameProp);
  var rootProps = useMemo(function () {
    return _objectSpread(_objectSpread({
      ref: ref
    }, props), {}, {
      className: rootClassName,
      onClick: isDisabled || isLoading ? null : onClick
    });
  }, [props, ref, rootClassName, isDisabled, isLoading, onClick]);
  var iconProps = useMemo(function () {
    var properties = {
      size: 'medium',
      name: icon
    };
    switch (type) {
      case 'secondary':
        return _objectSpread(_objectSpread({}, properties), {}, {
          color: theme.colors.orange500
        });
      case 'secondary-error':
        return _objectSpread(_objectSpread({}, properties), {}, {
          color: theme.colors.red500
        });
      case 'dashed':
      case 'terciary':
        return _objectSpread(_objectSpread({}, properties), {}, {
          color: theme.colors.neutral700
        });
      case 'promotion':
        return _objectSpread(_objectSpread({}, properties), {}, {
          color: theme.colors.neutral900
        });
      case 'primary':
      case 'primary-error':
      default:
        return _objectSpread(_objectSpread({}, properties), {}, {
          color: theme.colors.neutralBase
        });
    }
  }, [icon, theme.colors.neutral700, theme.colors.neutral900, theme.colors.neutralBase, theme.colors.orange500, theme.colors.red500, type]);
  var content = /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, isLoading && ALLOWED_LOADING_TYPES.includes(type) && /*#__PURE__*/React.createElement("div", {
    className: classes.loaderContainer
  }, /*#__PURE__*/React.createElement(Loader, _extends({
    size: LOADER_SIZES[size],
    color: "white"
  }, override.Loader))), icon && iconPosition === 'left' && /*#__PURE__*/React.createElement("div", _extends({
    className: classes.icon
  }, override.icon), /*#__PURE__*/React.createElement(Icon, iconProps)), children && /*#__PURE__*/React.createElement(Text, _extends({
    className: classes.Text
  }, override.Text), children), icon && iconPosition === 'right' && /*#__PURE__*/React.createElement("div", _extends({
    className: classes.icon
  }, override.icon), /*#__PURE__*/React.createElement(Icon, iconProps)));
  if (href) {
    return /*#__PURE__*/React.createElement("a", _extends({
      href: href,
      target: target
    }, rootProps, override.root), content);
  } else {
    return /*#__PURE__*/React.createElement("button", _extends({}, rootProps, {
      type: "button"
    }, override.root), content);
  }
});
Button.overrides = ['root', 'Text', 'Loader'];
Button.defaultProps = {
  size: 'medium',
  type: 'primary',
  overrides: {},
  iconPosition: 'left'
};
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  className: PropTypes.string,
  overrides: PropTypes.object,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['big', 'medium', 'small']),
  type: PropTypes.oneOf(['primary', 'primary-error', 'secondary', 'secondary-error', 'terciary', 'dashed', 'promotion']),
  isDisabled: PropTypes.bool,
  /** Use the whole container */
  isFullWidth: PropTypes.bool,
  isLoading: PropTypes.bool,
  /** Render the component as a tag <a/> with href */
  href: PropTypes.string,
  /** native <a/> target */
  target: PropTypes.string,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right'])
} : void 0;
export default /*#__PURE__*/memo(Button);